import { MIGRATION_BASE_URL } from './migrationTarget.Service';

/** Pelny flow logowania 2.0 (bramki + naprawa dostepow przez panel klienta VEHIS) i token sesyjny w odpowiedzi. */
const MIGRATION_LOGIN_PATH = '/traccar-extension/webapi/login/token';

/** Token 2.0 zapisany po udanej autoryzacji; czyszczony przy wylogowaniu 1.0 (localStorage.clear). */
const MIGRATION_TOKEN_KEY = 'migrationToken';

/** Po tylu ms rezygnujemy z proby - endpoint moze po drodze odpytywac panel klienta VEHIS. */
const MIGRATION_TIMEOUT_MS = 15000;

/**
 * ok = przenosimy do 2.0; denied = 2.0 odmowilo (401/403 - haslo inne niz w panelu VEHIS albo brak
 * dostepow); unavailable = blad techniczny (timeout, 5xx, rate limit) - user pracuje dalej na 1.0.
 */
export type MigrationLoginResult =
    | { status: 'ok'; token: string }
    | { status: 'denied' }
    | { status: 'unavailable' };

const DENIED: MigrationLoginResult = { status: 'denied' };
const UNAVAILABLE: MigrationLoginResult = { status: 'unavailable' };

/**
 * Konta wewnetrzne z tych domen ZOSTAJA na 1.0 - nie probujemy ich przenosic do 2.0,
 * niezaleznie od tego, czy maja konto w 2.0. Dopasowanie: dokladna domena albo poddomena
 * (np. adam@vehis.pl oraz adam@x.vehis.pl, ale nie adam@notvehis.pl).
 */
const MIGRATION_EXCLUDED_DOMAINS = ['vehis.pl', 'core-logic.com'];

/** Konkretne loginy, ktore ZOSTAJA na 1.0 niezaleznie od domeny (np. testerzy/partnerzy). Pisane malymi literami. */
const MIGRATION_EXCLUDED_EMAILS = [
    'instalatornew@emesipartnerzy.pl',
    'maksymilian.szamburski@gmail.com',
    'daniel.pastuszka96@gmail.com'
];

/**
 * Czy e-mail zostaje na 1.0 - albo domena wewnetrzna, albo konkretny login z listy wyjatkow.
 * Takich uzytkownikow NIE przenosimy automatycznie; dostaja pasek info o 2.0 (+ przycisk, jesli maja tam konto).
 */
export function isMigrationExcluded(email: string): boolean {
    const normalized = email.trim().toLowerCase();

    if (MIGRATION_EXCLUDED_EMAILS.indexOf(normalized) !== -1) {
        return true;
    }

    const at = normalized.lastIndexOf('@');
    if (at < 0) {
        return false;
    }

    const domain = normalized.slice(at + 1);

    return MIGRATION_EXCLUDED_DOMAINS.some(
        (excluded) => domain === excluded || domain.endsWith(`.${excluded}`)
    );
}

/**
 * Czy login jest w domenie wewnetrznej (MIGRATION_EXCLUDED_DOMAINS: vehis.pl, core-logic.com,
 * dokladnie albo poddomena). Dla nich pokazujemy osobny komunikat o wylaczonej synchronizacji.
 */
export function isSyncDisabledLogin(email: string): boolean {
    const at = email.lastIndexOf('@');
    if (at < 0) {
        return false;
    }

    const domain = email.slice(at + 1).trim().toLowerCase();

    return MIGRATION_EXCLUDED_DOMAINS.some(
        (excluded) => domain === excluded || domain.endsWith(`.${excluded}`)
    );
}

function withTimeout<T>(promise: Promise<T>, fallback: T): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((resolve) => setTimeout(() => resolve(fallback), MIGRATION_TIMEOUT_MS))
    ]);
}

async function requestMigrationToken(email: string, password: string): Promise<MigrationLoginResult> {
    // credentials: 'omit' - wynik oceniamy po statusie, sesje 2.0 zbuduje token z hasha.
    const response = await fetch(`${MIGRATION_BASE_URL}${MIGRATION_LOGIN_PATH}`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email, password }).toString()
    });

    if (response.status === 401 || response.status === 403) {
        return DENIED;
    }

    if (!response.ok) {
        return UNAVAILABLE;
    }

    const token = (await response.text()).trim();

    return token ? { status: 'ok', token } : UNAVAILABLE;
}

/**
 * Probuje zautoryzowac uzytkownika w SellTracking 2.0 tymi samymi danymi (Basic auth).
 * Sukces -> token sesyjny 2.0. Kazde niepowodzenie - brak konta, zle dane, CORS, timeout,
 * padniety serwis, haslo spoza zakresu btoa - zwraca null.
 * Probujemy dla KAZDEGO (takze domen wewnetrznych) - token mowi tylko "czy user MOZE wejsc do 2.0".
 * O automatycznym przeniesieniu decyduje _App: domeny wewnetrzne zostaja na 1.0 (z przyciskiem w pasku).
 */
export async function tryMigrationLogin(email: string, password: string): Promise<MigrationLoginResult> {
    if (!email || !password) {
        return UNAVAILABLE;
    }

    try {
        return await withTimeout(requestMigrationToken(email, password).catch(() => UNAVAILABLE), UNAVAILABLE);
    } catch (error) {
        return UNAVAILABLE;
    }
}

/** Zapisuje token 2.0 (albo czysci przy porazce). */
export function saveMigrationToken(token: string | null): void {
    if (token) {
        localStorage.setItem(MIGRATION_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(MIGRATION_TOKEN_KEY);
    }
}

/** Czy mamy token 2.0 - czyli czy ten uzytkownik MOZE wejsc do 2.0 (auto-redirect albo przycisk). */
export function hasMigrationToken(): boolean {
    return !!localStorage.getItem(MIGRATION_TOKEN_KEY);
}

/**
 * Sprzata stan 1.0 przy przejsciu do 2.0. Token 2.0 jest juz w URL (#token=), a sesja 1.0
 * (localStorage['token'] + migrationToken + reszta) nie jest juz potrzebna - user wchodzi do 2.0.
 * To samo robi wylogowanie 1.0 (localStorage.clear). URL trzeba zbudowac PRZED wywolaniem tej funkcji.
 */
export function clearMigrationSession(): void {
    localStorage.clear();
}

/**
 * Adres wejsciowy 2.0 z tokenem w hashu (#token=). Fragment nie leci do serwera ani do logow/Referera,
 * wiec token jest bezpieczniejszy niz w query. 2.0 musi czytac token z hasha - jesli dany build tego
 * nie robi, uzytkownik laduje na loginie 2.0 (do sprawdzenia na dev).
 */
export function buildMigrationUrl(): string {
    const token = localStorage.getItem(MIGRATION_TOKEN_KEY);

    return token ? `${MIGRATION_BASE_URL}#token=${encodeURIComponent(token)}` : MIGRATION_BASE_URL;
}


// WEBPACK FOOTER //
// ./src/components/MigrationModal/Services/migrationLogin.Service.ts